import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CircleCheckIcon } from 'icons/common/CircleCheck';
import { BrainIcon } from 'icons/common/Brain';
import { WrenchIcon } from 'icons/common/Wrench';
import { EyeIcon } from 'icons/common/Eye';
import { ZapIcon } from 'icons/common/Zap';
import { SparkleIcon } from 'icons/common/Sparkle';
import { TriangleAlertIcon } from 'icons/common/TriangleAlert';
import { type TraceStep, type TraceStepKind } from './RoutingAIChatTypes';

const TRACE_STEP_META: Record<TraceStepKind, { color: string; bg: string }> = {
  thought: { color: '#7C6BE0', bg: 'rgba(124,107,224,0.10)' },
  tool_call: { color: '#5B8DEF', bg: 'rgba(91,141,239,0.10)' },
  observation: { color: '#0F766E', bg: 'rgba(15,118,110,0.10)' },
  action: { color: '#E0A72A', bg: 'rgba(224,167,42,0.14)' },
  task: { color: '#0F766E', bg: 'rgba(15,118,110,0.10)' },
};

export const TRACE_KIND_LABELS: Record<TraceStepKind, string> = {
  thought: 'Thought',
  tool_call: 'Tool call',
  observation: 'Observation',
  action: 'Action',
  task: 'Task',
};

export const getTraceStepIcon = (kind: TraceStepKind) => {
  switch (kind) {
    case 'thought':
      return <BrainIcon size={10} color="currentColor" />;
    case 'tool_call':
      return <WrenchIcon size={10} color="currentColor" />;
    case 'observation':
      return <EyeIcon size={10} color="currentColor" />;
    case 'action':
      return <ZapIcon size={10} color="currentColor" />;
    case 'task':
      return <CircleCheckIcon size={10} color="currentColor" />;
    default:
      return <SparkleIcon size={10} color="currentColor" />;
  }
};

type StyledTraceStepIconProps = { traceKind: TraceStepKind };
type StyledTraceKindLabelProps = { traceKind: TraceStepKind };
type StyledTraceListProps = { showTopFade: boolean };

export const StyledTracePanel = styled.div({
  marginTop: '0.5rem',
  borderRadius: '0.375rem',
  border: '1px solid #e2e8f0',
  backgroundColor: 'rgba(248,250,252,0.7)',
  overflow: 'hidden',
});

export const StyledTraceHeader = styled.button({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.375rem 0.625rem',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#475569',
  ':hover': { backgroundColor: '#f1f5f9' },
});

export const StyledTraceHeaderLeft = styled.span({
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem',
});

export const StyledTraceSpin = styled.span({
  display: 'inline-block',
  animation: `${keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } })} 1s linear infinite`,
  lineHeight: 1,
});

export const StyledTraceTaskSpinner = styled.span({
  width: '0.75rem',
  height: '0.75rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid rgba(15,118,110,0.2)',
  borderTopColor: '#0f766e',
  borderRightColor: '#5cbdb9',
  borderRadius: '50%',
  animation: `${keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } })} 0.85s linear infinite`,
  boxShadow: '0 0 0 2px rgba(92,189,185,0.12), 0 0 0.5rem rgba(92,189,185,0.28)',
  flexShrink: 0,
});

export const StyledTraceList = styled.ol<StyledTraceListProps>(({ showTopFade }) => ({
  padding: '0.25rem 0.625rem 0.5rem',
  margin: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  maxHeight: '20rem',
  overflowY: 'auto',
  position: 'relative',
  ...(showTopFade && {
    maskImage: 'linear-gradient(to bottom, transparent 0, rgba(0,0,0,0.2) 1.5rem, rgba(0,0,0,0.72) 3.25rem, black 5rem, black 100%)',
    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, rgba(0,0,0,0.2) 1.5rem, rgba(0,0,0,0.72) 3.25rem, black 5rem, black 100%)',
  }),
}));

export const StyledTraceViewport = styled.div({
  position: 'relative',
});

export const StyledTraceNewEvents = styled.button({
  position: 'absolute',
  right: '0.625rem',
  bottom: '0.5rem',
  border: '1px solid #cbd5e1',
  borderRadius: '999px',
  padding: '0.2rem 0.55rem',
  backgroundColor: 'rgba(255,255,255,0.94)',
  color: '#0f766e',
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  fontSize: '0.625rem',
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
});

export const StyledTraceStepRow = styled.li({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
});

export const StyledTraceStepIcon = styled.span<StyledTraceStepIconProps>(({ traceKind }) => ({
  marginTop: '1px',
  width: '1.125rem',
  height: '1.125rem',
  borderRadius: '0.25rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: TRACE_STEP_META[traceKind].bg,
  color: TRACE_STEP_META[traceKind].color,
}));

export const StyledTraceStepBody = styled.div({
  minWidth: 0,
  flex: 1,
});

export const StyledTraceStepTitle = styled.div({
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.375rem',
  overflow: 'hidden',
});

export const StyledTraceKindLabel = styled.span<StyledTraceKindLabelProps>(({ traceKind }) => ({
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: TRACE_STEP_META[traceKind].color,
  fontFamily: 'FiraGO',
  flexShrink: 0,
}));

export const StyledTraceStepLabel = styled.span({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#1e293b',
  fontFamily: 'FiraGO',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const StyledTraceStepDetail = styled.div({
  fontSize: '0.6875rem',
  color: '#64748b',
  fontFamily: 'FiraGO',
  lineHeight: 1.4,
  marginTop: '0.125rem',
  wordBreak: 'break-word',
  p: {
    margin: '0 0 0.5rem',
  },
  'p:last-child': {
    marginBottom: 0,
  },
  'h1, h2, h3, h4, h5, h6': {
    margin: '0 0 0.5rem',
    color: '#334155',
    lineHeight: 1.3,
  },
  h1: {
    fontSize: '0.875rem',
  },
  h2: {
    fontSize: '0.8125rem',
  },
  h3: {
    fontSize: '0.75rem',
  },
  'ul, ol': {
    margin: '0 0 0.5rem',
    paddingLeft: '1.25rem',
  },
  li: {
    marginBottom: '0.2rem',
  },
  table: {
    width: '100%',
    margin: '0 0 0.5rem',
    borderCollapse: 'collapse',
    fontSize: '0.625rem',
  },
  'th, td': {
    border: '1px solid #d6e3ef',
    padding: '0.25rem 0.375rem',
    textAlign: 'left',
    verticalAlign: 'top',
  },
  th: {
    backgroundColor: '#f1f5f9',
    fontWeight: 600,
  },
  code: {
    padding: '0.05rem 0.15rem',
    borderRadius: '0.2rem',
    backgroundColor: '#eef2f7',
    fontFamily: 'monospace',
  },
  pre: {
    margin: '0 0 0.5rem',
    padding: '0.5rem',
    overflowX: 'auto',
    borderRadius: '0.25rem',
    backgroundColor: '#eef2f7',
  },
  'pre code': {
    padding: 0,
    backgroundColor: 'transparent',
  },
});

export const StyledTraceRunningLabel = styled.span({
  fontWeight: 400,
  letterSpacing: 'normal',
  textTransform: 'none',
  color: '#94a3b8',
});

export const StyledTraceTaskStatus = styled.span<{ status?: string }>(({ status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: '0.25rem',
  color: status === 'failed' ? '#b45309' : status === 'completed' ? '#059669' : '#64748b',
}));

export const AgentTracePanel = ({
  steps,
  streaming,
  labels,
}: {
  steps: TraceStep[];
  streaming: boolean;
  labels: { agentTrace: string; agentTraceRunning: string };
}) => {
  const [open, setOpen] = useState(true);
  const [atBottom, setAtBottom] = useState(true);
  const [hasNewEvents, setHasNewEvents] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);
  const listRef = useRef<HTMLOListElement | null>(null);
  const previousStepCount = useRef(steps.length);
  const hasActiveTask = steps.some(
    (step) => step.kind === 'task' && ['submitted', 'running'].includes(step.taskStatus || ''),
  );

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (atBottom) {
      list.scrollTop = list.scrollHeight;
      setShowTopFade(list.scrollHeight > list.clientHeight && list.scrollTop > 0);
      setHasNewEvents(false);
    } else if (steps.length > previousStepCount.current) {
      setHasNewEvents(true);
    }
    previousStepCount.current = steps.length;
  }, [atBottom, steps.length]);

  const handleTraceScroll = useCallback((event: React.UIEvent<HTMLOListElement>) => {
    const list = event.currentTarget;
    const isAtBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 8;
    setAtBottom(isAtBottom);
    setShowTopFade(list.scrollHeight > list.clientHeight && list.scrollTop > 0);
    if (isAtBottom) setHasNewEvents(false);
  }, []);

  if (!steps.length && !streaming) return null;

  return (
    <StyledTracePanel>
      <StyledTraceHeader type="button" onClick={() => setOpen((o) => !o)}>
        <StyledTraceHeaderLeft>
          {streaming ? (
            <StyledTraceSpin>
              <CircleCheckIcon size={11} color="#94a3b8" />
            </StyledTraceSpin>
          ) : hasActiveTask ? (
            <StyledTraceTaskSpinner aria-label="Task in progress" />
          ) : (
            <CircleCheckIcon size={11} color="#059669" />
          )}
          {labels.agentTrace}
          {' · '}
          {steps.length}
          {streaming && (
            <StyledTraceRunningLabel>{labels.agentTraceRunning}</StyledTraceRunningLabel>
          )}
        </StyledTraceHeaderLeft>
        {open ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
        )}
      </StyledTraceHeader>
      {open && (
        <StyledTraceViewport>
          <StyledTraceList
            ref={listRef}
            showTopFade={showTopFade}
            onScroll={handleTraceScroll}
          >
            {steps.map((step) => (
              <StyledTraceStepRow key={step.id}>
                <StyledTraceStepIcon traceKind={step.kind}>
                  {step.kind === 'task' && step.taskStatus === 'failed' ? (
                    <TriangleAlertIcon size={10} color="currentColor" />
                  ) : step.kind === 'task' && ['submitted', 'running'].includes(step.taskStatus || '') ? (
                    <StyledTraceTaskSpinner aria-label="Task in progress" />
                  ) : (
                    getTraceStepIcon(step.kind)
                  )}
                </StyledTraceStepIcon>
                <StyledTraceStepBody>
                  <StyledTraceStepTitle>
                    <StyledTraceKindLabel traceKind={step.kind}>
                      {TRACE_KIND_LABELS[step.kind]}
                    </StyledTraceKindLabel>
                    <StyledTraceStepLabel>{step.label}</StyledTraceStepLabel>
                    {step.kind === 'task' && step.taskCounts && (
                      <StyledTraceTaskStatus status={step.taskStatus}>
                        {step.taskCounts.resolved}/{step.taskCounts.requested}
                      </StyledTraceTaskStatus>
                    )}
                  </StyledTraceStepTitle>
                  {step.detail && (
                    <StyledTraceStepDetail>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{step.detail}</ReactMarkdown>
                    </StyledTraceStepDetail>
                  )}
                </StyledTraceStepBody>
              </StyledTraceStepRow>
            ))}
          </StyledTraceList>
          {hasNewEvents && (
            <StyledTraceNewEvents
              type="button"
              onClick={() => {
                const list = listRef.current;
                if (!list) return;
                list.scrollTop = list.scrollHeight;
                setAtBottom(true);
                setShowTopFade(list.scrollHeight > list.clientHeight && list.scrollTop > 0);
                setHasNewEvents(false);
              }}
            >
              New events ↓
            </StyledTraceNewEvents>
          )}
        </StyledTraceViewport>
      )}
    </StyledTracePanel>
  );
};
